<template>
    <div class="d-flex flex-column" v-bind:style="{ width: `${width}px` }">
        <!-- Slides -->
        <vueper-slides ref="galleryPicker" class="mb-1" v-bind:style="{ width: `${width}px` }" :fixed-height="`${height}px`" @slide="onSlideChanged">
            <!-- Images -->
            <vueper-slide class="position-relative" v-for="(image, index) in images" :key="index">
                <template v-slot:content>
                    <div title="Choose an image" class="base-image-input col-12 col-lg-6 p-0" v-bind:class="{'is-invalid': isValid === false }" v-bind:style="{ 'background-image': `url(${image.url})`,  width: `${width}px`,  height: `${height}px` }">
                    </div>     
                    <button class="d-flex justify-content-center align-items-center delete-button" @click="remove(index)"><CIcon name="cilMinus"></CIcon></button>
                </template>     
            </vueper-slide>
            <!-- Placeholder -->
            <vueper-slide v-if="!images.length">
                <template v-slot:content>
                    <div title="Choose an image" class="cursor-pointer base-image-input col-12 col-lg-6 p-0" v-bind:class="{'is-invalid': isValid === false }" @click="onChooseImageClicked" v-bind:style="{ width: `${width}px`,  height: `${height}px` }">
                        <span class="d-flex flex-column placeholder">
                            {{ $t('chooseImage') }}
                            <pre class="m-0 text-muted">{{readableRatio}}</pre>
                            <pre class="m-0 text-muted">.jpeg/.png</pre>
                        </span>   
                    </div>          
                </template>
            </vueper-slide>
        </vueper-slides>
        <!-- Picker -->
        <input ref="fileInput" type="file" accept=".jpg,.png" class="d-none" @change="onFileSelect" />
        <!-- Arrows -->
        <div v-if="max != 1" class="d-flex justify-content-between mt-1">
            <button v-bind:class="{ disabled: images.length == 1}" class="d-flex justify-content-center align-items-center" @click="$refs.galleryPicker.previous()"><CIcon name="cilArrowLeft"></CIcon></button>
            <button v-if="max == 0 || images.length < max" class="d-flex justify-content-center align-items-center" @click="onChooseImageClicked"><CIcon name="cilPlus"></CIcon></button>
            <button v-bind:class="{ disabled: currentSlide == 0}" class="d-flex justify-content-center align-items-center" @click="$refs.galleryPicker.next()"><CIcon name="cilArrowRight"></CIcon></button>
        </div>
        <!-- Cropper -->
        <BasePopUp v-if="showModal" v-model="showModal" @detailsClosed="showModal = false">
            <VueCropper v-show="selectedFile" ref="cropper" :src="selectedFile" alt="Source Image" :aspectRatio="applyRatio" :initialAspectRatio="applyRatio"></VueCropper>
            <CRow class="justify-content-between m-0 p-1">
                <CButton v-show="selectedFile" color="secondary" text @click="showModal = false">Cancel</CButton>
                <CButton v-show="selectedFile" color="primary" @click="confirmClicked">Crop</CButton>
            </CRow>
        </BasePopUp>
    </div>
</template>

<script>
import BasePopUp from "@/views/controls/popups/BasePopUp.vue";
import VueCropper from 'vue-cropperjs'
import 'cropperjs/dist/cropper.css'
import { getReducedFraction, convertToBase64 } from "@/helpers/utils.js";
import { VueperSlides, VueperSlide } from 'vueperslides'
import 'vueperslides/dist/vueperslides.css'

const MAX_SIZE = 5; // MB
const MAX_SIDE = 250; // Maximum value for width and height

export default {
    components: { VueCropper, BasePopUp, VueperSlides, VueperSlide },
    props: {
        value: null, // MAX 1 means object, others is array
        ratioWidth: {
            type: Number,
            default: 16
        },
        ratioHeight: {
            type: Number,
            default: 9
        },
        max: {
            type: Number,
            default: 0 // Infinite
        },
        isValid: 
        {
            type: Boolean,
            default: undefined
        }
    },
    data() {
        return {
            images: this.value ? (Array.isArray(this.value) ? this.value : [this.value]) : [],
            mime_type: '',
            fileName: '',
            selectedFile: '',
            showModal: false,
            currentSlide: 0
        }
    },
    computed:
    {
        applyRatio: function()
        {
            return this.ratioWidth / this.ratioHeight;
        },
        readableRatio: function()
        {
            return getReducedFraction(this.ratioWidth, this.ratioHeight);
        },
        width: function()
        {
            if(this.applyRatio <= 1)
            {
                return MAX_SIDE;
            }
            else
            {
                return this.applyRatio * MAX_SIDE;
            }
        },
        height: function()
        {
            if(this.applyRatio >= 1)
            {
                return MAX_SIDE;
            }
            else
            {
                return this.applyRatio * MAX_SIDE;
            }
        }
    },
    methods: {
        onSlideChanged(currentSlide)
        {
            this.currentSlide = currentSlide;
        },
        remove(index)
        {
            this.images.splice(index, 1);
        },
        onChooseImageClicked()
        {
            this.$refs.fileInput.click();
        },
        onFileSelect(e) 
        {
            try
            {
                const file = e.target.files[0];
                const maxSize = 1024 * 1024 * MAX_SIZE;
                this.mime_type = file.type;
                this.fileName = file.name;

                // Validations
                if (typeof FileReader != 'function') {
                    throw 'Sorry, FileReader API not supported';
                } 

                if(this.mime_type != 'image/jpg' && this.mime_type != 'image/jpeg' && this.mime_type != 'image/png')
                {
                    throw this.$t('imageErrorMessage2');
                }

                if(file.size > maxSize)
                {
                    throw this.$t('imageErrorMessage1');
                }

                // Process
                this.showModal = true

                const reader = new FileReader()
                reader.onload = (event) => {
                    this.showModal = true;
                    this.selectedFile = event.target.result;
                    this.$refs.cropper.replace(this.selectedFile);
                }
                reader.readAsDataURL(file);
            }
            catch(err)
            {
                alert(err);
            }
        },
        confirmClicked()
        {
            var imageData = this.$refs.cropper.getCroppedCanvas().toDataURL();
            this.$refs.cropper.getCroppedCanvas().toBlob((blob) => {
                blob.lastModifiedDate = new Date();
                blob.name = this.fileName;
                this.selectedFile = blob;

                convertToBase64(blob)
                .then(base64Image =>{
                    var newImage = {mime: this.mime_type, data: base64Image.split(',')[1], url: imageData};
                    this.images.push(newImage);
                    this.$refs.galleryPicker.goToSlide(this.images.length - 1);
                });
            }, this.mime_type);

            this.showModal = false;
        }
    },
    watch:
    {
        value: function(val)
        {
            this.images = val ? (Array.isArray(val) ? val : [val]) : []
        },
        showModal: function(val)
        {
            if(!val)
            {
                this.$refs.fileInput.value=null;
            }
        },
        images: function(val) 
        { 
            var image = val && val.length ? val[0] : null;
            var value = this.max == 1 ? image : val;
            this.$emit('input', value);
        }
    }
}
</script>

<style scoped>
.delete-button
{
    position: absolute;
    top: 0;
    right: 0;
}

.base-image-input {
  display: block;
  background-size: cover;
  background-position: center center;
  max-width: unset !important;
}

.placeholder {
  background: #F0F0F0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #333;
  font-size: 18px;
  font-family: Helvetica, Arial, Verdana, Tahoma, sans-serif;
}

.placeholder:hover {
  background: #E0E0E0;
}

.base-image-input.is-invalid .placeholder {
    background-color: palevioletred;
}

button
{
    border-color: #d8dbe0;
    border-top-color: rgb(216, 219, 224);
    border-right-color: rgb(216, 219, 224);
    border-bottom-color: rgb(216, 219, 224);
    border-left-color: rgb(216, 219, 224);
    border-radius: 0.25rem;
    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;
    border-bottom-right-radius: 0.25rem;
    border-bottom-left-radius: 0.25rem;
}

/deep/.cropper-container {
    max-height: 80vh;
}
</style>